import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Card, Form, Button, Alert, Tab, Tabs, ListGroup } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../api/axiosInstance';
import config from '../config';
import './Profile.css';

const getMediaUrl = (media) => {
  if (!media) return '';

  let mediaObject = media;
  if (Array.isArray(mediaObject)) {
    mediaObject = mediaObject[0];
  }
  if (mediaObject?.data) {
    mediaObject = Array.isArray(mediaObject.data) ? mediaObject.data[0] : mediaObject.data;
  }

  const rawUrl =
    mediaObject?.url ||
    mediaObject?.attributes?.url ||
    mediaObject?.formats?.medium?.url ||
    mediaObject?.formats?.small?.url ||
    mediaObject?.formats?.thumbnail?.url ||
    mediaObject?.attributes?.formats?.medium?.url ||
    mediaObject?.attributes?.formats?.small?.url ||
    mediaObject?.attributes?.formats?.thumbnail?.url;

  if (!rawUrl || typeof rawUrl !== 'string') return '';
  if (rawUrl.startsWith('http')) return rawUrl;

  const base = config.API_BASE?.replace(/\/$/, '') || window.location.origin;
  return `${base}${rawUrl.startsWith('/') ? '' : '/'}${rawUrl}`;
};

const ProfilePage = () => {
  const { user, updateProfile, isLoading } = useAuth();
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    gender: '',
    age: '',
    district: '',
  });
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddPetForm, setShowAddPetForm] = useState(false);
  const [editingPetId, setEditingPetId] = useState(null);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [removeProfilePicture, setRemoveProfilePicture] = useState(false);
  const [petProfilePictureFile, setPetProfilePictureFile] = useState(null);
  const [petProfilePicturePreview, setPetProfilePicturePreview] = useState('');
  const [petRemoveProfilePicture, setPetRemoveProfilePicture] = useState(false);
  const [newPetData, setNewPetData] = useState({
    name: '',
    species: 'dog',
    breed: '',
    gender: '',
    age: '',
    weight: '',
  });

  // Points and coupons state
  const [points, setPoints] = useState([]);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState('');
  const [redeemError, setRedeemError] = useState(false);

  // Derived display balance: prefer persisted user.pointsBalance, fallback to latest point record or sum
  const displayBalance = useMemo(() => {
    if (user?.pointsBalance != null) return user.pointsBalance;
    if (points && points.length > 0) {
      // find the most recent record by createdAt
      const latest = points.reduce((a, b) => (new Date(a.createdAt) > new Date(b.createdAt) ? a : b));
      if (latest && latest.balanceAfter != null) return latest.balanceAfter;
      // fallback sum of changes
      return points.reduce((sum, r) => sum + (r.change || 0), 0);
    }
    return 0;
  }, [user, points]);

  const fetchPets = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/pets/me');
      setPets(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch pets:', err);
    }
  }, []);

  const fetchPoints = useCallback(async () => {
    if (!user) return;
    setPointsLoading(true);
    try {
      const res = await axiosInstance.get('/points/me');
      setPoints(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch points:', err);
    } finally {
      setPointsLoading(false);
    }
  }, [user]);

  const fetchCoupons = useCallback(async () => {
    if (!user) return;
    setCouponsLoading(true);
    try {
      const res = await axiosInstance.get('/coupons/me');
      setCoupons(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
    } finally {
      setCouponsLoading(false);
    }
  }, [user]);

  const handleRedeem = async (e) => {
    e.preventDefault();
    setRedeemMessage('');
    setRedeemError(false);
    if (!couponCode || !couponCode.trim()) {
      setRedeemMessage('請輸入優惠券代碼');
      setRedeemError(true);
      return;
    }

    try {
      await axiosInstance.post('/coupons/redeem', { code: couponCode.trim() });
      setRedeemMessage('兌換成功');
      setCouponCode('');
      await fetchCoupons();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || '兌換失敗';
      setRedeemMessage(msg);
      setRedeemError(true);
    }
  };

  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        gender: user.gender || '',
        age: user.age || '',
        district: user.district || '',
      });
      setProfilePicturePreview(getMediaUrl(user.profilePicture));
      setProfilePictureFile(null);
      setRemoveProfilePicture(false);
      fetchPets();
      fetchPoints();
      fetchCoupons();
    }
  }, [user, fetchPets, fetchPoints, fetchCoupons]);

  const handleProfilePictureChange = (e) => {
    const file = e.target.files?.[0] || null;
    setProfilePictureFile(file);
    setRemoveProfilePicture(false);

    if (file) {
      setProfilePicturePreview(URL.createObjectURL(file));
    } else {
      setProfilePicturePreview(getMediaUrl(user?.profilePicture));
    }
  };

  const handleRemoveProfilePicture = () => {
    setProfilePictureFile(null);
    setProfilePicturePreview('');
    setRemoveProfilePicture(true);
  };

  const handlePetProfilePictureChange = (e) => {
    const file = e.target.files?.[0] || null;
    setPetProfilePictureFile(file);
    setPetRemoveProfilePicture(false);

    if (file) {
      setPetProfilePicturePreview(URL.createObjectURL(file));
    } else {
      const editingPet = pets.find((pet) => pet.id === editingPetId);
      setPetProfilePicturePreview(getMediaUrl(editingPet?.profilePicture));
    }
  };

  const handleRemovePetProfilePicture = () => {
    setPetProfilePictureFile(null);
    setPetProfilePicturePreview('');
    setPetRemoveProfilePicture(true);
  };


  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    const result = await updateProfile(profileData, profilePictureFile, removeProfilePicture);
    if (result.success) {
      setProfilePictureFile(null);
      setRemoveProfilePicture(false);
      setProfilePicturePreview(getMediaUrl(result.user?.profilePicture));
      setSuccess('資料已更新');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleDeletePet = async (petId) => {
    if (window.confirm('確定刪除此寵物嗎？')) {
      try {
        await axiosInstance.delete(`/pets/me/${petId}`);
        setPets((prev) => prev.filter((p) => p.id !== petId));
        setSuccess('寵物已刪除');
      } catch (err) {
        setError(err.response?.data?.error?.message || err.response?.data?.message || '刪除失敗');
      }
    }
  };

  const handleNewPetChange = (e) => {
    const { name, value } = e.target;
    setNewPetData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddPet = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newPetData.name || !newPetData.species) {
      setError('寵物名稱和種類為必填');
      return;
    }

    try {
      if (petProfilePictureFile) {
        const formData = new FormData();
        formData.append('name', newPetData.name);
        formData.append('species', newPetData.species);
        if (newPetData.breed) formData.append('breed', newPetData.breed);
        if (newPetData.gender) formData.append('gender', newPetData.gender);
        if (newPetData.age) formData.append('age', String(parseInt(newPetData.age, 10)));
        if (newPetData.weight) formData.append('weight', String(parseFloat(newPetData.weight)));
        formData.append('profilePicture', petProfilePictureFile);
        await axiosInstance.post('/pets/me', formData);
      } else {
        await axiosInstance.post('/pets/me', {
          name: newPetData.name,
          species: newPetData.species,
          breed: newPetData.breed || undefined,
          gender: newPetData.gender || undefined,
          age: newPetData.age ? parseInt(newPetData.age, 10) : undefined,
          weight: newPetData.weight ? parseFloat(newPetData.weight) : undefined,
        });
      }

      await fetchPets();
      setNewPetData({
        name: '',
        species: 'dog',
        breed: '',
        gender: '',
        age: '',
        weight: '',
      });
      setPetProfilePictureFile(null);
      setPetProfilePicturePreview('');
      setPetRemoveProfilePicture(false);
      setShowAddPetForm(false);
      setSuccess('寵物已新增');
    } catch (err) {
      setError(err.response?.data?.error?.message || err.response?.data?.message || '新增寵物失敗');
    }
  };

  const handleStartEditPet = (pet) => {
    setEditingPetId(pet.id);
    setNewPetData({
      name: pet.name || '',
      species: pet.species || 'dog',
      breed: pet.breed || '',
      gender: pet.gender || '',
      age: pet.age ?? '',
      weight: pet.weight ?? '',
    });
    setPetProfilePictureFile(null);
    setPetProfilePicturePreview(getMediaUrl(pet.profilePicture));
    setPetRemoveProfilePicture(false);
    setShowAddPetForm(true);
  };

  const handleCancelPetForm = () => {
    setShowAddPetForm(false);
    setEditingPetId(null);
    setNewPetData({
      name: '',
      species: 'dog',
      breed: '',
      gender: '',
      age: '',
      weight: '',
    });
    setPetProfilePictureFile(null);
    setPetProfilePicturePreview('');
    setPetRemoveProfilePicture(false);
  };

  const handleSavePet = async (e) => {
    if (editingPetId) {
      e.preventDefault();
      setError('');
      setSuccess('');

      if (!newPetData.name || !newPetData.species) {
        setError('寵物名稱和種類為必填');
        return;
      }

      try {
        if (petProfilePictureFile || petRemoveProfilePicture) {
          const formData = new FormData();
          formData.append('name', newPetData.name);
          formData.append('species', newPetData.species);
          if (newPetData.breed) formData.append('breed', newPetData.breed);
          if (newPetData.gender) formData.append('gender', newPetData.gender);
          if (newPetData.age) formData.append('age', String(parseInt(newPetData.age, 10)));
          if (newPetData.weight) formData.append('weight', String(parseFloat(newPetData.weight)));
          if (petProfilePictureFile) {
            formData.append('profilePicture', petProfilePictureFile);
          }
          if (petRemoveProfilePicture) {
            formData.append('removeProfilePicture', 'true');
          }
          await axiosInstance.put(`/pets/me/${editingPetId}`, formData);
        } else {
          await axiosInstance.put(`/pets/me/${editingPetId}`, {
            name: newPetData.name,
            species: newPetData.species,
            breed: newPetData.breed || undefined,
            gender: newPetData.gender || undefined,
            age: newPetData.age ? parseInt(newPetData.age, 10) : undefined,
            weight: newPetData.weight ? parseFloat(newPetData.weight) : undefined,
          });
        }

        await fetchPets();
        handleCancelPetForm();
        setSuccess('寵物資料已更新');
      } catch (err) {
        setError(err.response?.data?.error?.message || err.response?.data?.message || '更新寵物失敗');
      }
      return;
    }

    await handleAddPet(e);
  };

  if (isLoading) {
    return <div className="text-center p-5">Loading...</div>;
  }

  return (
    <Container className="profile-container py-5">
      <h1>我的資料</h1>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Tabs defaultActiveKey="profile" className="mb-4">
        <Tab eventKey="profile" title="個人資料">
          <Card>
            <Card.Body>
              <h4>編輯個人資料</h4>
              <Form onSubmit={handleProfileSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>頭像</Form.Label>
                  <div className="profile-picture-row">
                    <img
                      className="profile-picture-preview"
                      src={profilePicturePreview || '/default-cover.svg'}
                      alt="用戶頭像"
                    />
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                    />
                    {(profilePicturePreview || user?.profilePicture) && (
                      <Button
                        type="button"
                        variant="outline-danger"
                        onClick={handleRemoveProfilePicture}
                      >
                        移除頭像
                      </Button>
                    )}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>名字</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>姓氏</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>電話</Form.Label>
                  <Form.Control
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>性別</Form.Label>
                  <Form.Select
                    name="gender"
                    value={profileData.gender}
                    onChange={handleProfileChange}
                  >
                    <option value="">選擇...</option>
                    <option value="male">男</option>
                    <option value="female">女</option>
                    <option value="other">其他</option>
                    <option value="prefer_not_to_say">不想說</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>年齡</Form.Label>
                  <Form.Control
                    type="number"
                    name="age"
                    value={profileData.age}
                    onChange={handleProfileChange}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>地區</Form.Label>
                  <Form.Control
                    type="text"
                    name="district"
                    value={profileData.district}
                    onChange={handleProfileChange}
                    placeholder="香港地區"
                  />
                </Form.Group>

                <Button variant="primary" type="submit" disabled={loading}>
                  {loading ? '保存中...' : '保存'}
                </Button>
              </Form>

              <div className="mt-4">
                <h5>帳戶信息</h5>
                <p><strong>電郵：</strong> {user?.email}</p>
                <p><strong>會員等級：</strong> {user?.memberTier?.displayName || 'Free'}</p>
              </div>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="pets" title="寵物">
          <Card>
            <Card.Body>
              <h4>我的寵物</h4>
              {pets.length === 0 ? (
                <p>您還沒有添加任何寵物。</p>
              ) : (
                <ListGroup>
                  {pets.map((pet) => (
                    <ListGroup.Item key={pet.id} className="mb-3">
                      <div className="d-flex justify-content-between align-items-start gap-3">
                        <div className="d-flex gap-3 align-items-start">
                          <img
                            className="pet-picture-preview"
                            src={getMediaUrl(pet.profilePicture) || '/default-cover.svg'}
                            alt={`${pet.name || '寵物'}頭像`}
                          />
                          <div>
                          <h6>{pet.name}</h6>
                          <p className="text-muted mb-1">
                            {pet.species} {pet.breed && `- ${pet.breed}`}
                          </p>
                          {pet.age && <p className="text-muted mb-1">年齡：{pet.age}</p>}
                          {pet.weight && <p className="text-muted">體重：{pet.weight}kg</p>}
                          </div>
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleStartEditPet(pet)}
                          >
                            編輯
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeletePet(pet.id)}
                          >
                            刪除
                          </Button>
                        </div>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              {!showAddPetForm ? (
                <Button
                  variant="success"
                  className="mt-3"
                  onClick={() => {
                    setEditingPetId(null);
                    setNewPetData({
                      name: '',
                      species: 'dog',
                      breed: '',
                      gender: '',
                      age: '',
                      weight: '',
                    });
                    setPetProfilePictureFile(null);
                    setPetProfilePicturePreview('');
                    setPetRemoveProfilePicture(false);
                    setShowAddPetForm(true);
                  }}
                >
                  添加新寵物
                </Button>
              ) : (
                <Form className="mt-3" onSubmit={handleSavePet}>
                  <Form.Group className="mb-2">
                    <Form.Label>名稱</Form.Label>
                    <Form.Control
                      name="name"
                      value={newPetData.name}
                      onChange={handleNewPetChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>寵物頭像</Form.Label>
                    <div className="profile-picture-row">
                      <img
                        className="pet-picture-preview"
                        src={petProfilePicturePreview || '/default-cover.svg'}
                        alt="寵物頭像預覽"
                      />
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={handlePetProfilePictureChange}
                      />
                      {editingPetId && petProfilePicturePreview && (
                        <Button
                          type="button"
                          variant="outline-danger"
                          onClick={handleRemovePetProfilePicture}
                        >
                          移除頭像
                        </Button>
                      )}
                    </div>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>種類</Form.Label>
                    <Form.Select name="species" value={newPetData.species} onChange={handleNewPetChange}>
                      <option value="dog">狗</option>
                      <option value="cat">貓</option>
                      <option value="rabbit">兔</option>
                      <option value="bird">鳥</option>
                      <option value="hamster">倉鼠</option>
                      <option value="guinea_pig">天竺鼠</option>
                      <option value="other">其他</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>品種</Form.Label>
                    <Form.Control name="breed" value={newPetData.breed} onChange={handleNewPetChange} />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>性別</Form.Label>
                    <Form.Select name="gender" value={newPetData.gender} onChange={handleNewPetChange}>
                      <option value="">選擇...</option>
                      <option value="male">公</option>
                      <option value="female">母</option>
                      <option value="unknown">未知</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>年齡</Form.Label>
                    <Form.Control name="age" type="number" value={newPetData.age} onChange={handleNewPetChange} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>體重 (kg)</Form.Label>
                    <Form.Control name="weight" type="number" step="0.1" value={newPetData.weight} onChange={handleNewPetChange} />
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary">{editingPetId ? '更新寵物' : '保存寵物'}</Button>
                    <Button type="button" variant="outline-secondary" onClick={handleCancelPetForm}>取消</Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="rewards" title="積分與優惠券">
          <Card>
            <Card.Body>
              <h4>我的積分</h4>
              <p>
                <strong>積分餘額：</strong> {displayBalance ?? 0}
              </p>
              <div className="mb-3">
                <Button variant="outline-primary" size="sm" onClick={fetchPoints} disabled={pointsLoading}>
                  {pointsLoading ? '載入中...' : '重新整理點數'}
                </Button>
              </div>

              <h5 className="mt-3">點數紀錄</h5>
              {pointsLoading ? (
                <div>載入中...</div>
              ) : points.length === 0 ? (
                <p>尚無點數紀錄</p>
              ) : (
                <ListGroup>
                  {points.map((r) => (
                    <ListGroup.Item key={r.id} className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{r.change > 0 ? `+${r.change}` : r.change}</strong> — {r.reason || r.type}
                        <div className="text-muted" style={{ fontSize: '0.85em' }}>
                          {new Date(r.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-end">餘額：{r.balanceAfter ?? '-'}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

              <h4 className="mt-4">兌換優惠券</h4>
              <Form onSubmit={handleRedeem} className="mb-3">
                <Form.Group className="mb-2">
                  <Form.Label>優惠券代碼</Form.Label>
                  <Form.Control
                    name="couponCode"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="輸入優惠券代碼"
                  />
                </Form.Group>
                <Button type="submit" disabled={!couponCode}>
                  兌換
                </Button>
              </Form>
              {redeemMessage && <Alert variant={redeemError ? 'danger' : 'success'}>{redeemMessage}</Alert>}

              <h5 className="mt-3">我的優惠券</h5>
              {couponsLoading ? (
                <div>載入中...</div>
              ) : coupons.length === 0 ? (
                <p>目前沒有優惠券</p>
              ) : (
                <ListGroup>
                  {coupons.map((c) => (
                    <ListGroup.Item key={c.id} className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong>{c.code}</strong> — {c.title || c.description}
                        <div className="text-muted" style={{ fontSize: '0.85em' }}>
                          {c.status} {c.validTo ? `・到期：${new Date(c.validTo).toLocaleDateString()}` : ''}
                        </div>
                      </div>
                      <div className="text-end">使用次數：{c.timesUsed ?? 0}/{c.usageLimit ?? 1}</div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default ProfilePage;
